import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Icon from '../Icon';
import Tooltip from '../Tooltip';
import CapsulePopup from './CapsulePopup';
import { context, trace } from '@opentelemetry/api';

interface CapsulePopupInitProps {
  leftPosition: number | undefined;
  topPosition: number | undefined;
  show: string | null;
}

interface CapsuleTabProps {
  label: string;
  setPopupShow: (show: string | null) => void;
  setDrawerShow: (show: string) => void;
  onClick?: () => void;
  name: string;
  popupShow?: string | null;
  description?: string;
  styles?: string;
  icon?: string;
  expanded?: boolean;
  disabled?: boolean;
  error?: boolean;
  tooltip?: string;
  capsuleLeftPosition?: number;
  capsuleTopPosition?: number;
  capsuleWidth?: number;
  popupContent?: React.ReactNode;
  dynamicLeftOffset?: number;
  simpleMode: boolean;
}

const BaseCapsuleTab = styled.div<{
  focused?: boolean;
  disabled?: boolean;
  error?: boolean;
}>`
  background-color: ${props => (props.error ? '#ff0e0e' : props.focused && !props.disabled ? '#eff4fa' : '#fff')};
  background-color: ${props => (props.disabled ? '#f5f5f5' : '')};
  align-items: center;
  color: ${props => (props.error ? '#ffffff' : '#646a70')};
  padding: 15px 10px;
  font-size: 14px;
  ${props => (props.disabled ? '' : 'cursor: pointer;')}
  font-family: "Roboto", sans-serif;
  display: flex;
  text-align: left;
  box-sizing: border-box;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  ${props =>
    props.disabled
      ? ''
      : `&:hover {
    background-color: #eff4fa;
  }`}
  & > svg {
    margin-right: 10px;
  }
`;

const Label = styled.div<{ error?: boolean }>`
  font-weight: 400;
  font-size: 14px;
  padding-bottom: 2px;
`;

const Description = styled.div<{ error?: boolean }>`
  font-size: 12px;
  color: ${props => (props.error ? '#ffffff' : '#999')};
  white-space: nowrap;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CapsuleTab = ({
  label,
  description,
  name,
  popupShow,
  expanded,
  disabled,
  error,
  icon,
  onClick,
  setPopupShow,
  capsuleLeftPosition,
  capsuleTopPosition,
  capsuleWidth,
  popupContent,
  dynamicLeftOffset,
  simpleMode,
  setDrawerShow,
}: CapsuleTabProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);

  const [resizeTimeout, setResizeTimeout] = useState<any>(null);

  const [leftPosition, setLeftPosition] = useState<number | undefined>(undefined);
  const [topPosition, setTopPosition] = useState<number | undefined>(undefined);

  useEffect(() => {
    setLeftPosition(ref.current?.offsetLeft);
    setTopPosition(ref.current?.offsetTop);
    function handleResize() {
      clearTimeout(resizeTimeout);
      if (popupShow === name) {
        setResizeTimeout(
          setTimeout(() => {
            setLeftPosition(ref.current?.offsetLeft);
            setTopPosition(ref.current?.offsetTop);
          }, 10),
        );
      }
    }

    window.addEventListener('resize', handleResize);
  }, []);

  const _popupWidth = () => {
    return "auto";
  };

  return (
    <>
      <BaseCapsuleTab
        className="SeminargoCapsuleTab"
        ref={ref}
        focused={popupShow === name}
        disabled={disabled}
        error={error}
        onClick={() => {
          if (disabled) return;
          onClick && onClick();
          if (simpleMode) {
            setPopupShow && setPopupShow(popupShow === name ? null : name);
          }
          if (!simpleMode) {
            setDrawerShow && setDrawerShow(name);
            setPopupShow && setPopupShow(null);
          }
        }}
      >
        <Icon name={icon} />
        <div>
          <Label error={error}>{label}</Label>
          <Description error={error}>{description}</Description>
        </div>
      </BaseCapsuleTab>
      {popupShow === name && (
        <CapsulePopup
          ref={popupRef}
          leftPosition={(leftPosition || 0) + (capsuleLeftPosition || 0) - (dynamicLeftOffset || 0)}
          topPosition={(topPosition || 0) + (capsuleTopPosition || 0)}
          expanded={expanded || true}
          width={_popupWidth()}
          covered={false}
          fullScreen={false}
        >
          {popupContent}
        </CapsulePopup>
      )}
    </>
  );
};

export default CapsuleTab;
